import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import ManualBookingDialog from "@/components/ManualBookingDialog";
import TrialBanner from "@/components/TrialBanner";
import PlatformLogo from "@/components/PlatformLogo";
import {
  Scissors, Layout, Palette, PanelTop, PanelBottom, FileText,
  Image as ImageIcon, Search, FormInput, LogOut, ExternalLink, Home,
  CalendarDays, Scissors as ScissorsIcon, Mail, Users, CalendarClock,
  Settings, MessageSquare, Gift, Menu, X, BarChart3, CalendarPlus, Contact,
} from "lucide-react";

const PERMISSIONS = {
  salon_admin: new Set([
    "overview", "pages", "theme", "header", "footer", "blog", "gallery", "staff",
    "services", "availability", "bookings", "customers", "newsletter", "email_settings",
    "email_templates", "settings", "seo", "forms", "loyalty", "analytics",
  ]),
  staff_manager: new Set([
    "overview", "pages", "blog", "gallery", "services", "bookings", "customers", "newsletter", "seo", "forms", "loyalty",
  ]),
  staff_member: new Set(["overview", "bookings", "customers"]),
  super_admin: new Set(["*"]),
};

const buildNav = (t) => [
  { to: "/cms", label: "Übersicht", icon: Home, end: true, perm: "overview" },
  { to: "/cms/pages", label: "Seitenbuilder", icon: Layout, perm: "pages", feature: "cms_pages" },
  { to: "/cms/theme", label: "Theme Studio", icon: Palette, perm: "theme", feature: "theme_studio" },
  { to: "/cms/header", label: "Header", icon: PanelTop, perm: "header", feature: "header_footer" },
  { to: "/cms/footer", label: "Footer", icon: PanelBottom, perm: "footer", feature: "header_footer" },
  { to: "/cms/blog", label: t("blog"), icon: FileText, perm: "blog", feature: "blog" },
  { to: "/cms/gallery", label: t("gallery"), icon: ImageIcon, perm: "gallery", feature: "gallery" },
  { to: "/cms/staff", label: t("staff_plural"), icon: Users, perm: "staff", feature: "staff" },
  { to: "/cms/services", label: t("service_plural"), icon: ScissorsIcon, perm: "services", feature: "services" },
  { to: "/cms/availability", label: "Verfügbarkeit", icon: CalendarClock, perm: "availability", feature: "availability" },
  { to: "/cms/bookings", label: t("booking_plural"), icon: CalendarDays, perm: "bookings", feature: "bookings" },
  { to: "/cms/customers", label: "Kunden", icon: Contact, perm: "customers" },
  { to: "/cms/analytics", label: "Auswertung", icon: BarChart3, perm: "analytics", feature: "analytics" },
  { to: "/cms/newsletter", label: t("newsletter"), icon: Mail, perm: "newsletter", feature: "newsletter" },
  { to: "/cms/email", label: "E-Mail SMTP", icon: Mail, perm: "email_settings", feature: "email_smtp" },
  { to: "/cms/email-templates", label: "E-Mail-Vorlagen", icon: MessageSquare, perm: "email_templates", feature: "email_templates" },
  { to: "/cms/seo", label: "SEO", icon: Search, perm: "seo", feature: "seo" },
  { to: "/cms/forms", label: "Formulare", icon: FormInput, perm: "forms", feature: "forms" },
  { to: "/cms/loyalty", label: t("loyalty_program"), icon: Gift, perm: "loyalty", feature: "loyalty" },
  { to: "/cms/settings", label: "Einstellungen", icon: Settings, perm: "settings" },
];

const ROLE_LABELS = { salon_admin: "Admin", staff_manager: "Manager", staff_member: "Mitarbeiter", super_admin: "Super-Admin" };

export default function CmsLayout() {
  const { user, salon, plan, hasFeature, logout, t } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const role = user?.role || "staff_member";
  const allowed = PERMISSIONS[role] || new Set();
  const has = (p) => allowed.has("*") || allowed.has(p);
  const NAV = buildNav(t);
  const visibleNav = NAV.filter((n) => has(n.perm) && (!n.feature || hasFeature(n.feature)));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [manualBookingOpen, setManualBookingOpen] = useState(false);
  const canCreateBooking = has("bookings");

  // Close drawer on route change + ensure body scroll is restored
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Global keyboard shortcut: Shift + N opens the manual booking dialog
  useEffect(() => {
    if (!canCreateBooking) return undefined;
    const handler = (e) => {
      if (e.target?.tagName && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
      if (e.shiftKey && (e.key === "N" || e.key === "n")) {
        e.preventDefault();
        setManualBookingOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [canCreateBooking]);

  const sidebarContent = (
    <>
      <div className="p-5 border-b flex items-start justify-between" style={{ borderColor: "var(--mv-border)" }}>
        <div className="min-w-0">
          <Link to="/cms" className="flex items-center gap-2">
            <PlatformLogo imgClassName="h-7 w-auto object-contain">
              <Scissors className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
              <span className="font-display text-lg">StudioMV</span>
            </PlatformLogo>
          </Link>
          {salon && (
            <div className="mt-3">
              <p className="mv-label mb-1">{t("salon")}</p>
              <p className="text-sm font-medium truncate">{salon.name}</p>
              {plan && (
                <p
                  data-testid="current-plan-badge"
                  className="text-[10px] inline-block px-1.5 py-0.5 rounded mt-1"
                  style={{ background: "var(--mv-surface-2)", color: "var(--mv-primary)" }}
                >
                  Paket: {plan.name}
                </p>
              )}
              <a
                data-testid="public-site-link"
                href={`/salon/${salon.slug}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs flex items-center gap-1 mt-1 hover:underline"
                style={{ color: "var(--mv-primary)" }}
              >
                <ExternalLink className="w-3 h-3" strokeWidth={1.5} /> Live ansehen
              </a>
            </div>
          )}
        </div>
        <button
          type="button"
          data-testid="cms-drawer-close"
          onClick={() => setMobileOpen(false)}
          className="lg:hidden p-1.5 -m-1 rounded-md hover:bg-[var(--mv-surface-2)]"
          aria-label="Menü schließen"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      {/* User info + Abmelden — sticky at the top so it stays visible on long pages */}
      <div className="px-3 py-3 border-b" style={{ borderColor: "var(--mv-border)" }} data-testid="cms-user-block">
        <p className="text-xs px-1 truncate" style={{ color: "var(--mv-text-muted)" }}>{user?.email}</p>
        <p className="text-xs px-1 truncate" style={{ color: "var(--mv-primary)" }}>{ROLE_LABELS[role] || role}</p>
        <button
          data-testid="cms-logout-btn"
          onClick={() => { logout(); navigate("/login"); }}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-[var(--mv-surface-2)] transition-colors mt-2"
          style={{ color: "var(--mv-text-muted)" }}
        >
          <LogOut className="w-4 h-4" strokeWidth={1.5} /> Abmelden
        </button>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {canCreateBooking && (
          <button
            type="button"
            onClick={() => setManualBookingOpen(true)}
            data-testid="sidebar-new-booking-btn"
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 mb-3 text-sm font-medium text-white rounded-md transition-colors hover:opacity-90"
            style={{ background: "var(--mv-primary)" }}
            title="Neuer Termin (Tastenkürzel: Shift + N)"
          >
            <CalendarPlus className="w-4 h-4" strokeWidth={1.6} />
            Neuer Termin
          </button>
        )}
        {visibleNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`nav-${n.to.replace(/\//g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? "bg-[var(--mv-bg)] text-[var(--mv-text)] font-medium shadow-sm"
                  : "text-[var(--mv-text-muted)] hover:bg-[var(--mv-surface-2)]"
              }`
            }
          >
            <n.icon className="w-4 h-4" strokeWidth={1.5} />
            {n.label}
          </NavLink>
        ))}
      </nav>
    </>
  );

  return (
    <div className="min-h-screen lg:flex" style={{ background: "var(--mv-bg)" }}>
      {/* Mobile top bar */}
      <header
        className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 border-b"
        style={{ borderColor: "var(--mv-border)", background: "var(--mv-surface)" }}
      >
        <button
          type="button"
          data-testid="cms-hamburger-btn"
          onClick={() => setMobileOpen(true)}
          aria-label="Menü öffnen"
          className="p-1.5 -ml-1 rounded-md hover:bg-[var(--mv-surface-2)]"
        >
          <Menu className="w-6 h-6" strokeWidth={1.5} />
        </button>
        <Link to="/cms" className="flex items-center gap-2">
          <PlatformLogo imgClassName="h-6 w-auto object-contain">
            <Scissors className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
            <span className="font-display text-base">StudioMV</span>
          </PlatformLogo>
        </Link>
        <div className="flex items-center gap-1">
          {canCreateBooking && (
            <button
              type="button"
              onClick={() => setManualBookingOpen(true)}
              data-testid="mobile-new-booking-btn"
              className="p-1.5 rounded-md hover:bg-[var(--mv-surface-2)]"
              aria-label="Neuer Termin"
            >
              <CalendarPlus className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
            </button>
          )}
          {salon ? (
            <a
              href={`/salon/${salon.slug}`}
              target="_blank"
              rel="noreferrer"
              className="p-1.5 rounded-md hover:bg-[var(--mv-surface-2)]"
              aria-label="Live ansehen"
            >
              <ExternalLink className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
            </a>
          ) : <span className="w-8" />}
        </div>
      </header>

      {/* Desktop sidebar — sticky so logout & user info stay reachable on long pages */}
      <aside
        className="hidden lg:flex w-60 border-r flex-col flex-shrink-0 sticky top-0 h-screen"
        style={{ borderColor: "var(--mv-border)", background: "var(--mv-surface)" }}
      >
        {sidebarContent}
      </aside>

      {/* Mobile drawer + backdrop */}
      <div
        data-testid="cms-drawer-backdrop"
        onClick={() => setMobileOpen(false)}
        className={`lg:hidden fixed inset-0 z-40 transition-opacity ${mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        style={{ background: "rgba(0,0,0,0.45)" }}
      />
      <aside
        data-testid="cms-drawer"
        className={`lg:hidden fixed top-0 left-0 bottom-0 z-50 w-72 max-w-[85vw] flex flex-col border-r transition-transform duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ borderColor: "var(--mv-border)", background: "var(--mv-surface)" }}
        aria-hidden={!mobileOpen}
      >
        {sidebarContent}
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <TrialBanner />
        <Outlet context={{ openManualBooking: () => setManualBookingOpen(true) }} />
      </main>

      <ManualBookingDialog
        open={manualBookingOpen}
        onClose={() => setManualBookingOpen(false)}
        onCreated={() => { /* main bookings page reload via SWR handled there */ }}
      />
    </div>
  );
}
